import { showError, showSuccess } from '@calendly/alert';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { BulkActionsBar } from '@calendly/ui/components/bulk-actions-bar';
import JSZip from 'jszip';
import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeleteRecapsModal } from 'features/tracked_meetings';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { rootApi } from 'root-api';
import { isCalendlyForeignEventProvider } from 'shared/utils';
import { getFilenamePrefix, formatTranscriptContent } from './utils';

// Generate filename from tracked meeting data
const generateFilename = trackedMeeting => {
  const prefix = getFilenamePrefix(trackedMeeting);
  if (!prefix) {
    return I18n.t('publisher.meeting_intelligence.transcript.filename');
  }
  return I18n.t('publisher.meeting_intelligence.transcript.filename_with_prefix', {
    prefix
  });
};
const bulkActionsBarWrapperCss = "b9x86jv";
export const BulkRecapsActions = ({
  selectedRecaps,
  setSelectedRecaps,
  onDeletedRecapsChange
}) => {
  const [deletedRecaps, setDeletedRecaps] = useState([]);
  const [isDeleteRecapsModalOpen, openDeleteRecapsModal, closeDeleteRecapsModal] = useBooleanState(false);
  const dispatch = useDispatch();
  const track = useTrack();
  const previousDeletedRecapsLength = useRef(0);
  const handleBulkDelete = useCallback(() => {
    openDeleteRecapsModal();
  }, [openDeleteRecapsModal]);
  const handleDeleteRecapsModalSuccess = () => {
    setDeletedRecaps(prev => {
      const newDeletedRecaps = [...prev, ...selectedRecaps];
      return newDeletedRecaps;
    });
    dispatch(showSuccess(I18n.t('publisher.meeting_intelligence.details.delete_recap_modal.success')));
  };

  // Clear selected recaps whenever recaps are deleted
  useEffect(() => {
    if (deletedRecaps.length > previousDeletedRecapsLength.current) {
      setSelectedRecaps([]);
      previousDeletedRecapsLength.current = deletedRecaps.length;
    }
  }, [deletedRecaps, setSelectedRecaps]);
  useEffect(() => {
    onDeletedRecapsChange(deletedRecaps);
  }, [deletedRecaps, onDeletedRecapsChange]);
  const handleBulkDownload = useCallback(async () => {
    if (selectedRecaps.length === 1) {
      // Download single file as .txt
      const trackedMeetingUuid = selectedRecaps[0];
      const trackedMeetingResult = await dispatch(rootApi.endpoints.fetchTrackedMeetingWithSummary.initiate({
        meetingUuid: trackedMeetingUuid
      }));
      if (trackedMeetingResult.error || !trackedMeetingResult.data) {
        dispatch(showError(I18n.t('publisher.meeting_intelligence.transcript.fetching_error')));
        return;
      }
      const transcript = trackedMeetingResult.data.summaries?.transcript || [];
      const formattedContent = formatTranscriptContent(transcript);
      const filename = generateFilename(trackedMeetingResult.data);
      track({
        name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
        properties: {
          action: 'bulk_download',
          section: 'all',
          location: 'recaps_page',
          is_calendly_meeting: isCalendlyForeignEventProvider(trackedMeetingResult.data.foreignEventProvider),
          event_uuid: trackedMeetingResult.data.foreignEventId,
          mi_event_uuid: trackedMeetingUuid
        }
      });
      const blob = new Blob([formattedContent], {
        type: 'text/plain'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // Download multiple files as .zip containing .txt files
      try {
        const zip = new JSZip();
        const fetchPromises = selectedRecaps.map(async trackedMeetingUuid => {
          const trackedMeetingResult = await dispatch(rootApi.endpoints.fetchTrackedMeetingWithSummary.initiate({
            meetingUuid: trackedMeetingUuid
          }));
          if (trackedMeetingResult.error || !trackedMeetingResult.data) return null;
          const transcript = trackedMeetingResult.data.summaries?.transcript || [];
          const formattedContent = formatTranscriptContent(transcript);
          const filename = generateFilename(trackedMeetingResult.data);
          return {
            filename,
            content: formattedContent,
            data: trackedMeetingResult.data
          };
        });
        const results = await Promise.all(fetchPromises);
        if (results.some(result => result === null)) {
          dispatch(showError(I18n.t('publisher.meeting_intelligence.transcript.fetching_error')));
          return;
        }
        results.forEach((result, index) => {
          if (result) {
            zip.file(result.filename, result.content);
            track({
              name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
              properties: {
                action: 'bulk_download',
                section: 'all',
                location: 'recaps_page',
                is_calendly_meeting: isCalendlyForeignEventProvider(result.data?.foreignEventProvider),
                event_uuid: result.data?.foreignEventId,
                mi_event_uuid: selectedRecaps[index]
              }
            });
          }
        });
        const zipBlob = await zip.generateAsync({
          type: 'blob'
        });
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.download = I18n.t('publisher.meeting_intelligence.transcript.zip_filename');
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        dispatch(showError(I18n.t('publisher.meeting_intelligence.transcript.zip_error')));
      }
    }
  }, [selectedRecaps, dispatch, track]);
  const bulkActions = useMemo(() => [
  // Download button
  {
    label: I18n.t('publisher.meeting_intelligence.download_modal.download_button'),
    onClick: handleBulkDownload
  },
  // Delete button
  {
    label: I18n.t('publisher.meeting_intelligence.download_modal.delete_button'),
    onClick: handleBulkDelete
  }], [handleBulkDelete, handleBulkDownload]);
  return <>
      <div className={bulkActionsBarWrapperCss}>
        <BulkActionsBar selectedCount={selectedRecaps.length} onCancel={() => setSelectedRecaps([])} actions={bulkActions} />
      </div>
      <DeleteRecapsModal isOpen={isDeleteRecapsModalOpen} meetingUuids={selectedRecaps} onClose={closeDeleteRecapsModal} onSuccess={handleDeleteRecapsModalSuccess} />
    </>;
};

require("./bulk_recaps_actions.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./bulk_recaps_actions.js");